<template>
    <div class="login-page">
        <!-- ── Brand side ──────────────────────────────────────────────────── -->
        <section class="brand-panel">
            <!-- A lattice of presence dots with a sweep travelling through it: the shift
                 moving across the workforce, and an echo of the swoosh in the mark. -->
            <canvas ref="sweepCanvas" class="brand-canvas" aria-hidden="true"></canvas>
            <div class="brand-glow" aria-hidden="true"></div>

            <div class="brand-inner">
                <img src="/clinexgen-logo.png" alt="ClinexGen" class="brand-logo" />

                <div class="brand-copy">
                    <p class="brand-eyebrow">Workforce platform</p>
                    <h1 class="brand-title">Muster</h1>
                    <p class="brand-lead">
                        Every shift, every hour and every payslip — one record, kept straight.
                    </p>
                </div>

                <ul class="brand-list">
                    <li><span class="brand-bullet"></span>Attendance, shifts and handover</li>
                    <li><span class="brand-bullet"></span>Payroll and statutory returns</li>
                    <li><span class="brand-bullet"></span>Leave, onboarding and exits</li>
                </ul>
            </div>

            <p class="brand-foot">Innovate &nbsp;·&nbsp; Integrate &nbsp;·&nbsp; Elevate</p>
        </section>

        <!-- ── Form side ───────────────────────────────────────────────────── -->
        <section class="form-panel">
            <div class="form-inner">
                <!-- Carries the brand on narrow screens, where the panel above is hidden. -->
                <img src="/clinexgen-logo.png" alt="ClinexGen" class="form-logo" />

                <header class="form-head">
                    <h2 class="form-title">Sign in</h2>
                    <p class="form-sub">Welcome back. Use your Muster account.</p>
                </header>

                <form @submit.prevent="login" class="login-fields">
                    <div class="login-field">
                        <label for="email">User name</label>
                        <div class="login-input-wrap">
                            <i class="pi pi-user login-input-icon"></i>
                            <input
                                id="email"
                                v-model="loginData.username"
                                type="text"
                                placeholder="Enter your user name"
                                autocomplete="username"
                                required
                            />
                        </div>
                    </div>

                    <div class="login-field">
                        <label for="password">Password</label>
                        <div class="login-input-wrap">
                            <i class="pi pi-lock login-input-icon"></i>
                            <input
                                id="password"
                                v-model="loginData.password"
                                :type="showPassword ? 'text' : 'password'"
                                placeholder="Enter your password"
                                autocomplete="current-password"
                                required
                            />
                            <button
                                type="button"
                                class="login-eye-btn"
                                :aria-label="showPassword ? 'Hide password' : 'Show password'"
                                @click="showPassword = !showPassword"
                            >
                                <i :class="showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'"></i>
                            </button>
                        </div>
                    </div>

                    <div class="login-options">
                        <label class="login-remember">
                            <input type="checkbox" v-model="rememberMe" />
                            <span>Remember me</span>
                        </label>
                        <router-link to="/auth/forgot-password" class="login-forgot">Forgot password?</router-link>
                        <router-link to="/auth/signup" class="login-forgot">Start a free trial</router-link>
                    </div>

                    <button type="submit" class="login-submit" :disabled="loading">
                        <i v-if="loading" class="pi pi-spin pi-spinner"></i>
                        <template v-else>
                            <span>Sign In</span>
                            <i class="pi pi-arrow-right login-submit-arrow"></i>
                        </template>
                    </button>

                    <div v-if="errorMessage" class="login-error" role="alert">
                        <i class="pi pi-exclamation-circle"></i>
                        {{ errorMessage }}
                    </div>

                    <p class="login-help">Need any help? <a href="#" class="login-help-link">Click Here</a></p>
                </form>
            </div>
        </section>

        <!-- Role selection dialog -->
        <ONEDialog v-model="showRoleDialog" modal header="Choose Role" class="role-dialog">
            <div class="roles-container">
                <div v-for="role in roles" :key="role.RoleId" class="role-card"
                    @click="selectRole(role.UserId, role.RoleId, role.RoleName, role.DeptRoleMapId)">
                    <i class="pi pi-user role-icon"></i>
                    <span class="role-name">{{ role.RoleName }}</span>
                </div>
            </div>
        </ONEDialog>

        <!-- Loading overlay -->
        <div v-if="loading" class="login-loading-overlay">
            <ONEProgressSpinner />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../../stores/auth'
import type { LoginResult, UserData, UserDataToStore, RolesData } from '../../types/auth'
import LoginService from '../../services/auth/LoginService'
import saasAuthService from '../../services/saas/saasAuthService'

defineOptions({
    name: 'LoginView'
})

const router = useRouter()
const authStore = useAuthStore()
const loading = ref(false)
const errorMessage = ref('')
const showRoleDialog = ref(false)
const showPassword = ref(false)
const rememberMe = ref(false)
const roles = ref<LoginResult[]>([])

// Short-lived proof that the password was verified, held between the role
// dialog opening and the user choosing. Exchanged for an access token in
// selectRole; the server refuses it for anything else.
const selectionTicket = ref<string | null>(null)
const loginmsgdata = ref({
    loginmsg: '',
    class: ''
})
const loginData = ref({
    username: '',
    password: ''
})


/* ─── Shift sweep ─────────────────────────────────────────────────────────────
 * A lattice of presence dots with a wave travelling through it: dots ahead of the
 * wave sit dim, dots it reaches light up in the brand orange. It reads as a shift
 * moving across the workforce, which is what this system is built around, and it
 * echoes the swoosh in the ClinexGen mark.
 *
 * Painted on a canvas rather than as DOM nodes -- at this density it would be a
 * few hundred elements, and this is a login page, not a document.
 * ────────────────────────────────────────────────────────────────────────── */
const sweepCanvas = ref<HTMLCanvasElement>()
let sweepFrame = 0
let stopSweep: (() => void) | null = null

const startSweep = () => {
    const canvas = sweepCanvas.value
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const GAP = 26
    let width = 0
    let height = 0

    const resize = () => {
        // Cap the device ratio: a 3x phone would cost four times the fill for no
        // visible gain on dots this small.
        const ratio = Math.min(window.devicePixelRatio || 1, 2)
        width = canvas.clientWidth
        height = canvas.clientHeight
        canvas.width = Math.round(width * ratio)
        canvas.height = Math.round(height * ratio)
        ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
    }

    const draw = (time: number) => {
        if (!width || !height) resize()
        ctx.clearRect(0, 0, width, height)

        // One pass every eight seconds, starting off-panel on the left.
        const travel = still ? 0.46 : ((time / 8000) % 1.35) - 0.2
        const crest = travel * width

        for (let y = GAP; y < height; y += GAP) {
            // Bowing the front by row turns a flat wall into a sweep.
            const front = crest + Math.sin((y / height) * Math.PI * 1.15) * 110

            for (let x = GAP; x < width; x += GAP) {
                const reach = Math.max(0, 1 - Math.abs(x - front) / 165)
                const lit = reach * reach

                ctx.beginPath()
                ctx.arc(x, y, 1.1 + lit * 2.5, 0, Math.PI * 2)
                ctx.fillStyle = lit > 0.02
                    ? 'rgba(249, 141, 60, ' + (0.14 + lit * 0.8) + ')'
                    : 'rgba(148, 180, 214, 0.15)'
                ctx.fill()
            }
        }

        if (!still) sweepFrame = requestAnimationFrame(draw)
    }

    resize()
    window.addEventListener('resize', resize)
    if (still) draw(0)
    else sweepFrame = requestAnimationFrame(draw)

    stopSweep = () => {
        cancelAnimationFrame(sweepFrame)
        window.removeEventListener('resize', resize)
    }
}

onMounted(() => {
    errorMessage.value = ''
    loginmsgdata.value.class = ''
    startSweep()
})

onBeforeUnmount(() => {
    // Leaving the frame loop running would keep painting a canvas that is gone.
    stopSweep?.()
})

const isLoginResult = (item: unknown): item is LoginResult => {
    return typeof item === 'object' && item !== null && 'RoleId' in item
}

const login = async () => {
    try {
        loading.value = true
        errorMessage.value = ''

        localStorage.removeItem('token')
        localStorage.removeItem('UsersData')
        localStorage.removeItem('RolesData')
        localStorage.removeItem('saas_access_token')
        authStore.clearAuth()

        // Verify the credential against the SIGNED-TOKEN endpoint first.
        //
        // It is a superset of the legacy check, so it replaces it rather than
        // supplementing it:
        //   - a self-service tenant is verified against its PBKDF2 credential.
        //     The legacy path CANNOT sign these users in at all: provisioning
        //     writes an unusable '!pbkdf2!' marker into UserMaster.Password
        //     precisely so the plaintext comparison can never match, so going
        //     to GetRoles first would tell every new customer their password
        //     was wrong.
        //   - an existing pre-SaaS user has no credential row, so the server
        //     falls back to the same plaintext comparison GetRoles performs and
        //     they sign in exactly as before.
        // Either way we end up holding a real token, which is what the licence
        // and the tenant boundary rest on.
        const saasResult = await saasAuthService.login(loginData.value.username, loginData.value.password)

        if (saasResult?.Status === 'SUCCESS' && saasResult.User) {
            await selectRole(
                saasResult.User.UserId,
                saasResult.User.RoleId,
                saasResult.User.RoleName,
                saasResult.User.DeptRoleMapId,
                saasResult.User,
            )
            return
        }

        if (saasResult?.Status === 'ROLE_SELECTION_REQUIRED') {
            // Hold the ticket so the chosen role can be exchanged for a token.
            selectionTicket.value = saasResult.SelectionTicket
            roles.value = saasResult.Roles.map(r => ({
                UserId: 0,
                RoleId: r.RoleId,
                RoleName: r.RoleName,
                DeptRoleMapId: r.DeptRoleMapId,
            })) as LoginResult[]
            showRoleDialog.value = true
            return
        }

        if (saasResult?.Status === 'ACCOUNT_LOCKED') {
            errorMessage.value = saasResult.Message
            loginmsgdata.value.class = 'error'
            return
        }

        if (saasResult?.Status && saasResult.Status !== 'INVALID_CREDENTIALS') {
            // NO_ROLE_ASSIGNED and ACCOUNT_INACTIVE both carry a message worth
            // showing: they say what is actually wrong, which "invalid password"
            // would hide.
            errorMessage.value = saasResult.Message
            loginmsgdata.value.class = 'error'
            return
        }

        // Fall through to the legacy path. Reached when the credential was
        // rejected, and kept as a safety net for any account shape the new
        // endpoint does not recognise - it ends with the same "invalid" message.
        const response = await LoginService.GetRoles(loginData.value.username, loginData.value.password)
        console.log('Login response:', response)

        if (Array.isArray(response.result)) {
            if (response.result.length === 0) {
                errorMessage.value = 'No roles found'
                loginmsgdata.value.class = 'error'
                return
            }

            const firstResult = response.result[0]
            if (typeof firstResult === 'string') {
                if (firstResult === 'AccountLocked') {
                    // 4 consecutive failed attempts — server has flipped
                    // IsLocked=1 on UserMaster. Only an HR/Admin can clear
                    // this from the User Master page.
                    errorMessage.value = 'Account is locked after 4 failed sign-in attempts. Please contact your HR / admin to unblock.'
                    loginmsgdata.value.class = 'error'
                } else if (firstResult === 'Invalid') {
                    errorMessage.value = 'Invalid username or password'
                    loginmsgdata.value.class = 'error'
                }
                return
            }

            if (isLoginResult(firstResult)) {
                if (response.result.length > 1) {
                    roles.value = response.result.filter(isLoginResult)
                    showRoleDialog.value = true
                } else {
                    await selectRole(firstResult.UserId, firstResult.RoleId, firstResult.RoleName, firstResult.DeptRoleMapId)
                }
            }
        }
    } catch (error) {
        console.error('Login error:', error)
        errorMessage.value = 'An error occurred during login'
        loginmsgdata.value.class = 'error'
    } finally {
        loading.value = false
    }
}

const selectRole = async (
    userId: number,
    roleId: number,
    roleName: string,
    deptRoleMapId: number,
    // The profile the signed-token login already returned, used when the legacy
    // lookup cannot supply one. See the fallback note below.
    saasUser?: import('../../services/saas/saasAuthService').AuthUser | null,
) => {
    try {
        loading.value = true
        showRoleDialog.value = false

        // Get the signed token FIRST, before anything that might need the
        // profile it carries. When the user came through the role dialog the
        // ticket has to be exchanged for the role they actually chose, and that
        // exchange is what returns their profile.
        let resolvedUser = saasUser ?? null

        if (!saasAuthService.getToken()) {
            try {
                const tokenResult = selectionTicket.value
                    ? await saasAuthService.selectRole(selectionTicket.value, roleId)
                    : await saasAuthService.login(loginData.value.username, loginData.value.password, roleId)

                selectionTicket.value = null
                if (tokenResult?.Status === 'SUCCESS' && tokenResult.User) resolvedUser = tokenResult.User
            } catch (tokenError) {
                // Not fatal on its own: the legacy lookup below may still
                // succeed for a pre-existing user. It only becomes fatal if
                // that lookup also comes back empty.
                console.warn('Could not obtain a signed token.', tokenError)
            }
        }

        let userData: UserData | null = null

        try {
            const userResponse = await LoginService.Login(userId, 1)
            if (Array.isArray(userResponse?.result) && userResponse.result.length > 0) {
                userData = userResponse.result[0] as UserData
            }
        } catch (legacyError) {
            console.warn('Legacy user lookup failed; falling back to the signed-token profile.', legacyError)
        }

        if (!userData) {
            // GetLoginVerification resolves its data from AppCache's static
            // lists, which are loaded once and held for 24 hours. A tenant that
            // signed up after those lists were populated is simply not in them,
            // so this returns an empty array and a brand-new customer could not
            // sign in until the cache expired or the API restarted.
            //
            // The signed-token login reads the database directly and has already
            // returned everything needed here, so it is used instead. Fields
            // that only the legacy payload carries (HelpUrl, TaxType, PriceType)
            // are left at their defaults - a new tenant has no values for them
            // anyway, since OrganizationConfig has no row until one is
            // configured.
            if (!resolvedUser) throw new Error('Invalid user data received')

            userData = {
                ID: resolvedUser.UserId,
                UserName: resolvedUser.UserName,
                OrganizationId: resolvedUser.OrganizationId,
                OrganizationName: resolvedUser.OrganizationName,
                BranchId: resolvedUser.BranchId ?? 0,
                IsAdmin: resolvedUser.IsAdmin ? 1 : 0,
                IsActive: true,
                MobileNumber: '',
            } as unknown as UserData
        }

        console.log('User data:', userData)

        // Generate token from user data
        const token = btoa(JSON.stringify({
            userId,
            roleId,
            organizationId: userData.OrganizationId,
            timestamp: new Date().getTime()
        }))

        // Update auth store
        authStore.setToken(token)
        authStore.setUser({
            id: userId,
            organizationId: userData.OrganizationId,
            roleId: roleId,
            deptRoleMapId: deptRoleMapId,
            email: loginData.value.username,
            name: userData.UserName,
            branchId: userData.BranchId,
            isAdmin: userData.IsAdmin == 1 ? true : false
        })
        authStore.setEntityId(0)
        authStore.setOrganizationId(userData.OrganizationId)
        authStore.setBranchId(userData.BranchId)

        // Store in localStorage
        localStorage.setItem('UsersData', JSON.stringify(userData))
        localStorage.setItem('token', token)
        localStorage.setItem('RolesData', JSON.stringify({
            RoleId: roleId,
            RoleName: roleName,
            DeptRoleMapId: deptRoleMapId
        }))

        // The signed token was already obtained at the top of this function,
        // before the profile lookup that may depend on it. The `token` written
        // above is the legacy browser-built value, kept so existing screens
        // continue to work unchanged.

        console.log('Auth store updated, navigating to dashboard')
        router.push({ name: 'hrDashboard' })
    } catch (error) {
        console.error('Role selection error:', error)
        errorMessage.value = 'An error occurred while processing your login'
        loginmsgdata.value.class = 'error'
    } finally {
        loading.value = false
    }
}
</script>

<style scoped>
/* ═══════════════════════════════════════════════════════════════════════════
   Muster sign-in

   Palette taken from the ClinexGen mark: a navy ground with the orange as the
   single accent. Neutrals are biased cool so they sit in the same family as the
   ground rather than reading as generic greys.
   ══════════════════════════════════════════════════════════════════════════ */
.login-page {
    --navy: #0a1e33;
    --navy-deep: #071726;
    --orange: #f97316;
    --orange-lift: #ffb169;
    --paper: #ffffff;
    --ink: #0f172a;
    --muted: #64748b;
    --hairline: #e2e8f0;
    --field: #f6f8fb;

    min-height: 100vh;
    min-height: 100dvh;
    display: grid;
    grid-template-columns: 1.05fr 0.95fr;
    grid-template-rows: minmax(0, 1fr);
    background: var(--paper);
    color: var(--ink);
    font-family: 'IBM Plex Sans', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* ─── Brand side ─────────────────────────────────────────────────────────── */
.brand-panel {
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 56px 56px 40px;
    background:
        radial-gradient(120% 90% at 12% 8%, #123452 0%, transparent 60%),
        linear-gradient(160deg, var(--navy) 0%, var(--navy-deep) 100%);
    color: #eaf1f8;
}

.brand-canvas {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    display: block;
}

/* The swoosh from the mark, blown up and pushed into the corner. */
.brand-glow {
    position: absolute;
    right: -22%;
    bottom: -30%;
    width: 78%;
    aspect-ratio: 1;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(249, 115, 22, 0.30) 0%, rgba(249, 115, 22, 0) 68%);
    pointer-events: none;
}

.brand-inner {
    position: relative;
    z-index: 1;
    max-width: 460px;
    margin: auto 0;
}

/* Keeps the lattice from running through the copy without hiding it. */
.brand-panel::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(100deg, rgba(7, 23, 38, 0.74) 0%, rgba(7, 23, 38, 0.30) 32%, rgba(7, 23, 38, 0) 60%);
    pointer-events: none;
}

.brand-logo {
    width: 208px;
    max-width: 60%;
    height: auto;
    display: block;
    margin-bottom: 52px;
    padding: 10px 16px;
    border-radius: 12px;
    /* The artwork is drawn for a light ground, so it gets its own card on the navy. */
    background: rgba(255, 255, 255, 0.94);
}

.brand-eyebrow {
    margin: 0 0 12px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--orange-lift);
}

.brand-title {
    margin: 0 0 18px;
    /* Outranks the app-wide h1 rule in assets/compact-density.css. */
    font-size: clamp(44px, 5vw, 64px) !important;
    font-weight: 700;
    line-height: 1;
    letter-spacing: -0.03em;
    text-wrap: balance;
}

.brand-lead {
    margin: 0;
    max-width: 24em;
    font-size: 16px;
    line-height: 1.6;
    color: #a8bed4;
}

.brand-list {
    margin: 40px 0 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 14px;
    font-size: 14px;
    color: #cbdaea;
}

.brand-list li {
    display: flex;
    align-items: center;
    gap: 12px;
}

.brand-bullet {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--orange);
    box-shadow: 0 0 0 4px rgba(249, 115, 22, 0.16);
    flex-shrink: 0;
}

.brand-foot {
    position: absolute;
    left: 56px;
    bottom: 38px;
    z-index: 1;
    margin: 0;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: #5c7794;
}

/* ─── Form side ──────────────────────────────────────────────────────────── */
.form-panel {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 40px;
    background: var(--paper);
}

.form-inner {
    width: 100%;
    max-width: 384px;
}

/* Only shown once the brand panel drops away. */
.form-logo {
    display: none;
    width: 176px;
    height: auto;
    margin: 0 auto 28px;
}

.form-head { margin-bottom: 32px; }

.form-title {
    margin: 0 0 6px;
    /* Outranks the app-wide h2 rule in assets/compact-density.css. */
    font-size: 27px !important;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: var(--ink);
}

.form-sub {
    margin: 0;
    font-size: 14px;
    color: var(--muted);
}

/* ─── Fields ─────────────────────────────────────────────────────────────── */
.login-fields { display: flex; flex-direction: column; gap: 18px; }

.login-field { display: flex; flex-direction: column; gap: 7px; }

.login-field label {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: #475569;
}

.login-input-wrap {
    position: relative;
    display: flex;
    align-items: center;
}

.login-input-icon {
    position: absolute;
    left: 14px;
    font-size: 13px;
    color: #94a3b8;
    pointer-events: none;
    transition: color 0.18s ease;
    z-index: 1;
}

.login-input-wrap input {
    width: 100%;
    height: 46px;
    padding: 0 14px 0 40px;
    font-size: 14px;
    font-family: inherit;
    color: var(--ink);
    background: var(--field);
    border: 1px solid transparent;
    border-radius: 10px;
    outline: none;
    transition: background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
}

.login-input-wrap input::placeholder { color: #9aa8b8; }

.login-input-wrap input:hover { background: #f1f5f9; }

.login-input-wrap input:focus {
    background: var(--paper);
    border-color: var(--orange);
    box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.16);
}

.login-eye-btn {
    position: absolute;
    right: 6px;
    display: grid;
    place-items: center;
    width: 34px;
    height: 34px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: #94a3b8;
    cursor: pointer;
    transition: color 0.18s ease, background 0.18s ease;
}

.login-eye-btn:hover { color: var(--ink); background: #eef2f7; }

.login-eye-btn:focus-visible,
.login-remember input:focus-visible,
.login-forgot:focus-visible,
.login-help-link:focus-visible {
    outline: 2px solid var(--orange);
    outline-offset: 2px;
}

/* ─── Options row ────────────────────────────────────────────────────────── */
.login-options {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-top: 2px;
    font-size: 13px;
}

.login-remember {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #475569;
    cursor: pointer;
    user-select: none;
}

.login-remember input {
    width: 15px;
    height: 15px;
    accent-color: var(--orange);
    cursor: pointer;
}

.login-forgot {
    color: var(--orange);
    font-weight: 600;
    text-decoration: none;
}

.login-forgot:hover { text-decoration: underline; }

/* ─── Submit ─────────────────────────────────────────────────────────────── */
.login-submit {
    position: relative;
    height: 48px;
    margin-top: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    font-size: 15px;
    font-weight: 600;
    font-family: inherit;
    color: #ffffff;
    background: linear-gradient(135deg, #fb8a3c 0%, var(--orange) 55%, #e35d0b 100%);
    border: 0;
    border-radius: 10px;
    cursor: pointer;
    box-shadow: 0 8px 20px -8px rgba(234, 88, 12, 0.65);
    transition: transform 0.16s ease, box-shadow 0.16s ease, filter 0.16s ease;
}

.login-submit:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 12px 26px -8px rgba(234, 88, 12, 0.72);
    filter: saturate(1.06);
}

.login-submit:active:not(:disabled) { transform: translateY(0); }

.login-submit:focus-visible { outline: 2px solid var(--navy); outline-offset: 3px; }

.login-submit:disabled { opacity: 0.62; cursor: not-allowed; box-shadow: none; }

.login-submit-arrow {
    font-size: 12px;
    transition: transform 0.16s ease;
}

.login-submit:hover:not(:disabled) .login-submit-arrow { transform: translateX(3px); }

/* ─── Error and help ─────────────────────────────────────────────────────── */
.login-error {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    padding: 11px 13px;
    font-size: 13px;
    line-height: 1.45;
    color: #b42318;
    background: #fef3f2;
    border: 1px solid #fecdca;
    border-radius: 9px;
}

.login-error i { margin-top: 1px; font-size: 14px; flex-shrink: 0; }

.login-help {
    margin: 6px 0 0;
    text-align: center;
    font-size: 13px;
    color: var(--muted);
}

.login-help-link {
    color: var(--orange);
    font-weight: 600;
    text-decoration: none;
}

.login-help-link:hover { text-decoration: underline; }

/* ─── Role dialog ────────────────────────────────────────────────────────── */
.roles-container {
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-width: 280px;
    padding: 4px 0;
}

.role-card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 13px 15px;
    background: var(--field);
    border: 1px solid var(--hairline);
    border-radius: 10px;
    cursor: pointer;
    transition: background 0.16s ease, border-color 0.16s ease, transform 0.16s ease;
}

.role-card:hover {
    background: #fff7ed;
    border-color: var(--orange);
    transform: translateX(2px);
}

.role-icon { font-size: 15px; color: var(--orange); }

.role-name { font-size: 14px; font-weight: 500; color: var(--ink); }

/* ─── Loading overlay ────────────────────────────────────────────────────── */
.login-loading-overlay {
    position: fixed;
    inset: 0;
    z-index: 50;
    display: grid;
    place-items: center;
    background: rgba(10, 30, 51, 0.32);
    backdrop-filter: blur(2px);
}

/* ─── Narrow screens ─────────────────────────────────────────────────────── */
@media (max-width: 900px) {
    /* The panel cannot take half a phone screen, but hiding it left the page with no
       product identity at all -- just a logo and a form. It becomes a band instead,
       keeping the sweep and the name above the fold. */
    .login-page {
        grid-template-columns: 1fr;
        grid-template-rows: auto minmax(0, 1fr);
    }

    .brand-panel {
        justify-content: flex-start;
        padding: 30px 26px 28px;
    }

    .brand-inner { margin: 0; max-width: none; }

    .brand-logo { width: 152px; max-width: 46%; margin-bottom: 22px; }

    .brand-eyebrow { margin-bottom: 7px; }

    .brand-title { font-size: 36px !important; margin-bottom: 0; }

    .brand-lead,
    .brand-list,
    .brand-foot { display: none; }

    .form-panel {
        align-items: flex-start;
        padding: 38px 24px 44px;
    }

    .form-head { margin-bottom: 26px; }
}

@media (max-width: 400px) {
    .brand-panel { padding: 24px 20px 24px; }
    .brand-logo { width: 132px; margin-bottom: 18px; }
    .brand-title { font-size: 31px !important; }
    .form-title { font-size: 24px !important; }
}

/* Movement is decoration here; the canvas paints one still frame instead. */
@media (prefers-reduced-motion: reduce) {
    .login-submit,
    .login-submit-arrow,
    .role-card {
        transition: none;
    }
}
</style>
